import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export type AppToastItem = {
  id: string;
  message: string;
  title?: string;
  variant?: 'success' | 'danger' | 'warning' | 'info';
  durationMs?: number;
};

type AppToastContainerProps = {
  toasts: AppToastItem[];
  onDismiss: (id: string) => void;
};

const TOAST_EXIT_MS = 220;
const TOAST_ENTER_MS = 320;
const TOAST_SHIFT_MS = 280;

const ToastItem: React.FC<{
  toast: AppToastItem;
  onDismiss: (id: string) => void;
  bindRef: (id: string, element: HTMLDivElement | null) => void;
}> = ({ toast, onDismiss, bindRef }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const closeTimerRef = useRef<number | null>(null);

  const requestClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    closeTimerRef.current = window.setTimeout(() => onDismiss(toast.id), TOAST_EXIT_MS);
  }, [isClosing, onDismiss, toast.id]);

  useEffect(() => {
    const raf = window.requestAnimationFrame(() => setIsVisible(true));
    return () => window.cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const timeout = window.setTimeout(() => requestClose(), toast.durationMs ?? 5000);
    return () => window.clearTimeout(timeout);
  }, [toast.id, toast.durationMs, requestClose]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    };
  }, []);

  return (
    <div
      ref={(element) => bindRef(toast.id, element)}
      className={`alert alert-${toast.variant || 'info'} shadow-sm border-0 mb-2 app-toast-item ${
        isClosing ? 'app-toast-out' : isVisible ? 'app-toast-in' : 'app-toast-pre'
      }`}
      role="alert"
    >
      <div className="d-flex align-items-start justify-content-between gap-2">
        <div>
          {toast.title ? <div className="fw-semibold mb-1 app-toast-title">{toast.title}</div> : null}
          <div className="mb-0">{toast.message}</div>
        </div>
        <button
          type="button"
          className="btn-close"
          aria-label="Fechar"
          onClick={requestClose}
        />
      </div>
    </div>
  );
};

const AppToastContainer: React.FC<AppToastContainerProps> = ({ toasts, onDismiss }) => {
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const prevTopsRef = useRef<Map<string, number>>(new Map());

  useLayoutEffect(() => {
    const nextTops = new Map<string, number>();

    toasts.forEach((toast) => {
      const element = itemRefs.current.get(toast.id);
      if (!element) return;

      const top = element.getBoundingClientRect().top;
      nextTops.set(toast.id, top);

      const prevTop = prevTopsRef.current.get(toast.id);
      if (typeof prevTop !== 'number') return;

      const deltaY = prevTop - top;
      if (Math.abs(deltaY) < 1) return;

      element.animate(
        [
          { transform: `translateY(${deltaY}px)` },
          { transform: 'translateY(0)' }
        ],
        {
          duration: TOAST_SHIFT_MS,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)'
        }
      );
    });

    prevTopsRef.current = nextTops;
  }, [toasts]);

  const bindRef = (id: string, element: HTMLDivElement | null) => {
    if (element) {
      itemRefs.current.set(id, element);
      return;
    }
    itemRefs.current.delete(id);
    prevTopsRef.current.delete(id);
  };

  if (!toasts.length) return null;

  return (
    <>
      <style>
        {`
          .app-toast-item {
            transform: translateX(0);
            opacity: 1;
            transition: transform ${TOAST_ENTER_MS}ms cubic-bezier(0.22, 1, 0.36, 1), opacity ${TOAST_ENTER_MS}ms ease;
            will-change: transform, opacity;
            font-size: 0.875rem;
            line-height: 1.25rem;
            padding: 0.55rem 0.7rem;
          }

          .app-toast-title {
            font-size: 0.82rem;
            line-height: 1.1rem;
          }

          .app-toast-item .btn-close {
            transform: scale(0.85);
            margin-top: 0.1rem;
          }

          .app-toast-pre {
            transform: translateX(-24px);
            opacity: 0;
          }

          .app-toast-in {
            transform: translateX(0);
            opacity: 1;
          }

          .app-toast-out {
            transform: translateX(-24px);
            opacity: 0;
          }
        `}
      </style>
      <div
      className="position-fixed bottom-0 start-0 p-3"
      style={{ zIndex: 2000, width: 'min(340px, calc(100vw - 1rem))', pointerEvents: 'none' }}
    >
      <div style={{ pointerEvents: 'auto' }}>
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} bindRef={bindRef} />
        ))}
      </div>
      </div>
    </>
  );
};

export default AppToastContainer;
